import React, { useState, useRef, useMemo, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import JoditEditor from 'jodit-react';
import { toast } from 'react-toastify';
import apiService from '../../services/apiService';
import { getJoditConfig } from '../../utils/joditConfig';
import MultiSelect from '../../components/MultiSelect';
import ConfirmModal from '../../components/ConfirmModal';

const EditLocation = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const aboutEditor = useRef(null);
  const [showBackModal, setShowBackModal] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    countryId: '',
    stateId: '',
    months: [],
    latitude: '',
    longitude: '',
    author: '',
    metaTitle: '',
    metaScript: '',
    metaKeyword: '',
    metaDescription: '',
    shortDescription: '',
    weatherInfo: { summer: '', winter: '', monsoon: '' },
    bestTimeToVisit: '',
    estimatedBudget: '',
    language: '',
    duration: '',
    farFrom: '',
    address: '',
    packages: []
  });

  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [months, setMonths] = useState([]);
  const [packages, setPackages] = useState([]);
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [faqs, setFaqs] = useState([]);
  const [recentFaqs, setRecentFaqs] = useState([]);
  const [faqsPackage, setFaqsPackage] = useState([]);
  const [recentFaqsPackage, setRecentFaqsPackage] = useState([]);
  const [faqsThingsToDo, setFaqsThingsToDo] = useState([]);
  const [recentFaqsThingsToDo, setRecentFaqsThingsToDo] = useState([]);
  const [faqPlacesToVisit, setFaqPlacesToVisit] = useState([]);
  const [recentFaqPlacesToVisit, setRecentFaqPlacesToVisit] = useState([]);
  const [keywordLinks, setKeywordLinks] = useState([]);
  const [about, setAbout] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [countrySearch, setCountrySearch] = useState('');
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [stateSearch, setStateSearch] = useState('');
  const [showStateDropdown, setShowStateDropdown] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');
  const countryDropdownRef = useRef(null);
  const stateDropdownRef = useRef(null);

  const editorConfig = useMemo(() => getJoditConfig(), []);

  useEffect(() => {
    fetchLocationData();

    window.history.pushState(null, '', window.location.href);
    const handlePopState = () => {
      window.history.pushState(null, '', window.location.href);
      setShowBackModal(true);
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [id]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (countryDropdownRef.current && !countryDropdownRef.current.contains(event.target)) {
        setShowCountryDropdown(false);
      }
      if (stateDropdownRef.current && !stateDropdownRef.current.contains(event.target)) {
        setShowStateDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (formData.countryId && !loading) {
      fetchStates(formData.countryId);
    }
  }, [formData.countryId, loading]);

  const fetchLocationData = async () => {
    try {
      const response = await apiService.get(`editLocation/${id}`);
      const locationData = response.data.data;
      
      setCountries(response.data.country || []);
      setMonths(response.data.months || []);
      setStates(response.data.states || []);

      const allPackages = (response.data.packages || []).map(p => ({
        ...p,
        name: p.vendorId?.business_name ? `${p.name} (${p.vendorId.business_name})` : p.name
      }));
      setPackages(allPackages);

      setFormData({
        name: locationData.name || '',
        countryId: locationData.countryId || '',
        stateId: locationData.stateId || '',
        months: locationData.months || [],
        latitude: locationData.latitude || '',
        longitude: locationData.longitude || '',
        author: locationData.author || '',
        metaTitle: locationData.metaTitle || '',
        metaScript: locationData.metaScript || '',
        metaKeyword: locationData.metaKeyword || '',
        metaDescription: locationData.metaDescription || '',
        shortDescription: locationData.shortDescription || '',
        weatherInfo: locationData.weatherInfo || { summer: '', winter: '', monsoon: '' },
        bestTimeToVisit: locationData.bestTimeToVisit || '',
        estimatedBudget: locationData.estimatedBudget || '',
        language: locationData.language || '',
        duration: locationData.duration || '',
        farFrom: locationData.farFrom || '',
        address: locationData.address || '',
        packages: locationData.packages || []
      });

      const selectedCountry = response.data.country?.find(c => c._id === locationData.countryId);
      if (selectedCountry) setCountrySearch(selectedCountry.name);

      const selectedState = response.data.states?.find(s => s._id === locationData.stateId);
      if (selectedState) setStateSearch(selectedState.name);

      setAbout(locationData.about || '');
      setImagePreview(locationData.image ? `${process.env.REACT_APP_MEDIA_URL}${locationData.image}` : '');
      setFaqs(locationData.faq && locationData.faq.length > 0 ? locationData.faq : []);
      setRecentFaqs(locationData.recentFaq && locationData.recentFaq.length > 0 ? locationData.recentFaq : []);
      setFaqsPackage(locationData.faqPackage && locationData.faqPackage.length > 0 ? locationData.faqPackage : []);
      setRecentFaqsPackage(locationData.recentFaqPackage && locationData.recentFaqPackage.length > 0 ? locationData.recentFaqPackage : []);
      setFaqsThingsToDo(locationData.faqThingsToDo && locationData.faqThingsToDo.length > 0 ? locationData.faqThingsToDo : []);
      setRecentFaqsThingsToDo(locationData.recentFaqThingsToDo && locationData.recentFaqThingsToDo.length > 0 ? locationData.recentFaqThingsToDo : []);
      setFaqPlacesToVisit(locationData.faqPlacestovisit && locationData.faqPlacestovisit.length > 0 ? locationData.faqPlacestovisit : []);
      setRecentFaqPlacesToVisit(locationData.recentFaqPlacestovisit && locationData.recentFaqPlacestovisit.length > 0 ? locationData.recentFaqPlacestovisit : []);
      setKeywordLinks(locationData.keywordLink && locationData.keywordLink.length > 0 ? locationData.keywordLink : []);
      
      setLoading(false);
    } catch (error) {
      toast.error('Failed to fetch location data');
      console.error('Fetch Error:', error);
      setLoading(false);
    }
  };

  const fetchStates = async (countryId) => {
    try {
      const response = await apiService.get(`stateList/${countryId}`);
      setStates(response.data || []);
    } catch (error) {
      console.error('Error fetching states:', error);
    }
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const selectCountry = (country) => {
    setFormData({ ...formData, countryId: country._id, stateId: '' });
    setCountrySearch(country.name);
    setStateSearch('');
    setShowCountryDropdown(false);
  };

  const selectState = (state) => {
    setFormData({ ...formData, stateId: state._id });
    setStateSearch(state.name);
    setShowStateDropdown(false);
  };

  const filteredCountries = countries.filter(c => 
    c.name.toLowerCase().includes(countrySearch.toLowerCase())
  );

  const filteredStates = states.filter(s => 
    s.name.toLowerCase().includes(stateSearch.toLowerCase())
  );

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size must be 5MB or less');
        e.target.value = '';
        return;
      }
      setImage(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const addFaq = () => setFaqs([...faqs, { question: '', answer: '' }]);
  const removeFaq = (index) => setFaqs(faqs.filter((_, i) => i !== index));
  const updateFaq = (index, field, value) => {
    const updated = [...faqs];
    updated[index][field] = value;
    setFaqs(updated);
  };

  const addRecentFaq = () => setRecentFaqs([...recentFaqs, { question: '', answer: '' }]);
  const removeRecentFaq = (index) => setRecentFaqs(recentFaqs.filter((_, i) => i !== index));
  const updateRecentFaq = (index, field, value) => {
    const updated = [...recentFaqs];
    updated[index][field] = value;
    setRecentFaqs(updated);
  };

  const addRecentFaqPackage = () => setRecentFaqsPackage([...recentFaqsPackage, { question: '', answer: '' }]);
  const removeRecentFaqPackage = (index) => setRecentFaqsPackage(recentFaqsPackage.filter((_, i) => i !== index));
  const updateRecentFaqPackage = (index, field, value) => {
    const updated = [...recentFaqsPackage];
    updated[index][field] = value;
    setRecentFaqsPackage(updated);
  };

  const addFaqPackage = () => setFaqsPackage([...faqsPackage, { question: '', answer: '' }]);
  const removeFaqPackage = (index) => setFaqsPackage(faqsPackage.filter((_, i) => i !== index));
  const updateFaqPackage = (index, field, value) => {
    const updated = [...faqsPackage];
    updated[index][field] = value;
    setFaqsPackage(updated);
  };

  const addRecentFaqThingsToDo = () => setRecentFaqsThingsToDo([...recentFaqsThingsToDo, { question: '', answer: '' }]);
  const removeRecentFaqThingsToDo = (index) => setRecentFaqsThingsToDo(recentFaqsThingsToDo.filter((_, i) => i !== index));
  const updateRecentFaqThingsToDo = (index, field, value) => {
    const updated = [...recentFaqsThingsToDo];
    updated[index][field] = value;
    setRecentFaqsThingsToDo(updated);
  };

  const addFaqThingsToDo = () => setFaqsThingsToDo([...faqsThingsToDo, { question: '', answer: '' }]);
  const removeFaqThingsToDo = (index) => setFaqsThingsToDo(faqsThingsToDo.filter((_, i) => i !== index));
  const updateFaqThingsToDo = (index, field, value) => {
    const updated = [...faqsThingsToDo];
    updated[index][field] = value;
    setFaqsThingsToDo(updated);
  };

  const addFaqPlacesToVisit = () => setFaqPlacesToVisit([...faqPlacesToVisit, { question: '', answer: '' }]);
  const removeFaqPlacesToVisit = (index) => setFaqPlacesToVisit(faqPlacesToVisit.filter((_, i) => i !== index));
  const updateFaqPlacesToVisit = (index, field, value) => {
    const updated = [...faqPlacesToVisit];
    updated[index][field] = value;
    setFaqPlacesToVisit(updated);
  };

  const addRecentFaqPlacesToVisit = () => setRecentFaqPlacesToVisit([...recentFaqPlacesToVisit, { question: '', answer: '' }]);
  const removeRecentFaqPlacesToVisit = (index) => setRecentFaqPlacesToVisit(recentFaqPlacesToVisit.filter((_, i) => i !== index));
  const updateRecentFaqPlacesToVisit = (index, field, value) => {
    const updated = [...recentFaqPlacesToVisit];
    updated[index][field] = value;
    setRecentFaqPlacesToVisit(updated);
  };

  const addKeywordLink = () => setKeywordLinks([...keywordLinks, { keyword: '', link: '' }]);
  const removeKeywordLink = (index) => setKeywordLinks(keywordLinks.filter((_, i) => i !== index));
  const updateKeywordLink = (index, field, value) => {
    const updated = [...keywordLinks];
    updated[index][field] = value;
    setKeywordLinks(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    const data = new FormData();
    data.append('id', id);
    data.append('name', formData.name);
    data.append('countryId', formData.countryId);
    if (formData.stateId) data.append('stateId', formData.stateId);
    formData.months.forEach(month => data.append('months[]', month));
    data.append('latitude', formData.latitude);
    data.append('longitude', formData.longitude);
    data.append('author', formData.author);
    data.append('about', about);
    data.append('metaTitle', formData.metaTitle);
    data.append('metaScript', formData.metaScript);
    data.append('metaKeyword', formData.metaKeyword);
    data.append('metaDescription', formData.metaDescription);
    data.append('shortDescription', formData.shortDescription);
    data.append('weatherInfo[summer]', formData.weatherInfo.summer);
    data.append('weatherInfo[winter]', formData.weatherInfo.winter);
    data.append('weatherInfo[monsoon]', formData.weatherInfo.monsoon);
    data.append('bestTimeToVisit', formData.bestTimeToVisit);
    data.append('estimatedBudget', formData.estimatedBudget);
    data.append('language', formData.language);
    data.append('duration', formData.duration);
    data.append('farFrom', formData.farFrom);
    data.append('address', formData.address);
    
    if (formData.packages && formData.packages.length > 0) {
      formData.packages.forEach(pkg => data.append('packages[]', pkg));
    }
    
    if (image) data.append('image', image);

    const filteredFaqs = faqs.filter(f => f.question?.trim() || f.answer?.trim());
    if (filteredFaqs.length === 0) {
      data.append('delete_all_faq', '1');
    } else {
      data.append('delete_all_faq', '0');
      filteredFaqs.forEach((faq, i) => {
        data.append(`faq[${i}][question]`, faq.question || '');
        data.append(`faq[${i}][answer]`, faq.answer || '');
      });
    }

    const filteredRecentFaqs = recentFaqs.filter(f => f.question?.trim() || f.answer?.trim());
    if (filteredRecentFaqs.length === 0) {
      data.append('delete_all_recentFaq', '1');
    } else {
      data.append('delete_all_recentFaq', '0');
      filteredRecentFaqs.forEach((faq, i) => {
        data.append(`recentFaq[${i}][question]`, faq.question || '');
        data.append(`recentFaq[${i}][answer]`, faq.answer || '');
      });
    }

    const filteredFaqsPackage = faqsPackage.filter(f => f.question?.trim() || f.answer?.trim());
    if (filteredFaqsPackage.length === 0) {
      data.append('delete_all_faqPackage', '1');
    } else {
      data.append('delete_all_faqPackage', '0');
      filteredFaqsPackage.forEach((faq, i) => {
        data.append(`faqPackage[${i}][question]`, faq.question || '');
        data.append(`faqPackage[${i}][answer]`, faq.answer || '');
      });
    }

    const filteredRecentFaqsPackage = recentFaqsPackage.filter(f => f.question?.trim() || f.answer?.trim());
    if (filteredRecentFaqsPackage.length === 0) {
      data.append('delete_all_recentFaqPackage', '1');
    } else {
      data.append('delete_all_recentFaqPackage', '0');
      filteredRecentFaqsPackage.forEach((faq, i) => {
        data.append(`recentFaqPackage[${i}][question]`, faq.question || '');
        data.append(`recentFaqPackage[${i}][answer]`, faq.answer || '');
      });
    }

    const filteredFaqsThingsToDo = faqsThingsToDo.filter(f => f.question?.trim() || f.answer?.trim());
    if (filteredFaqsThingsToDo.length === 0) {
      data.append('delete_all_faqThingsToDo', '1');
    } else {
      data.append('delete_all_faqThingsToDo', '0');
      filteredFaqsThingsToDo.forEach((faq, i) => {
        data.append(`faqThingsToDo[${i}][question]`, faq.question || '');
        data.append(`faqThingsToDo[${i}][answer]`, faq.answer || '');
      });
    }

    const filteredRecentFaqsThingsToDo = recentFaqsThingsToDo.filter(f => f.question?.trim() || f.answer?.trim());
    if (filteredRecentFaqsThingsToDo.length === 0) {
      data.append('delete_all_recentFaqThingsToDo', '1');
    } else {
      data.append('delete_all_recentFaqThingsToDo', '0');
      filteredRecentFaqsThingsToDo.forEach((faq, i) => {
        data.append(`recentFaqThingsToDo[${i}][question]`, faq.question || '');
        data.append(`recentFaqThingsToDo[${i}][answer]`, faq.answer || '');
      });
    }

    const filteredFaqPlacesToVisit = faqPlacesToVisit.filter(f => f.question?.trim() || f.answer?.trim());
    if (filteredFaqPlacesToVisit.length === 0) {
      data.append('delete_all_faqPlacestovisit', '1');
    } else {
      data.append('delete_all_faqPlacestovisit', '0');
      filteredFaqPlacesToVisit.forEach((faq, i) => {
        data.append(`faqPlacestovisit[${i}][question]`, faq.question || '');
        data.append(`faqPlacestovisit[${i}][answer]`, faq.answer || '');
      });
    }

    const filteredRecentFaqPlacesToVisit = recentFaqPlacesToVisit.filter(f => f.question?.trim() || f.answer?.trim());
    if (filteredRecentFaqPlacesToVisit.length === 0) {
      data.append('delete_all_recentFaqPlacestovisit', '1');
    } else {
      data.append('delete_all_recentFaqPlacestovisit', '0');
      filteredRecentFaqPlacesToVisit.forEach((faq, i) => {
        data.append(`recentFaqPlacestovisit[${i}][question]`, faq.question || '');
        data.append(`recentFaqPlacestovisit[${i}][answer]`, faq.answer || '');
      });
    }

    const filteredKeywordLinks = keywordLinks.filter(k => k.keyword?.trim() || k.link?.trim());
    if (filteredKeywordLinks.length === 0) {
      data.append('delete_all_keywordLink', '1');
    } else {
      data.append('delete_all_keywordLink', '0');
      filteredKeywordLinks.forEach((kl, i) => {
        data.append(`keywordLink[${i}][keyword]`, kl.keyword || '');
        data.append(`keywordLink[${i}][link]`, kl.link || '');
      });
    }

    try {
      await apiService.postFormData('updateLocation', data);
      toast.success('Location updated successfully');
      navigate('/location');
    } catch (error) {
      toast.error('Failed to update location');
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#009ef7]"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'basic', label: 'Basic Info', icon: 'bi-info-circle' },
    { id: 'details', label: 'Details', icon: 'bi-list-ul' },
    { id: 'locationFaq', label: 'Location FAQ', icon: 'bi-question-circle' },
    { id: 'packageFaq', label: 'Package FAQ', icon: 'bi-box-seam' },
    { id: 'thingsToDoFaq', label: 'ThingsToDo FAQ', icon: 'bi-activity' },
    { id: 'placesToVisitFaq', label: 'PlacesToVisit FAQ', icon: 'bi-geo-alt' },
    { id: 'seo', label: 'SEO', icon: 'bi-search' }
  ];

  return (
    <div className="p-0">
      <div className="mb-6">
        <nav className="text-sm mb-4">
          <Link to="/dashboard" className="text-gray-500 hover:text-gray-700">Home</Link>
          <span className="mx-2 text-gray-400">/</span>
          <span className="text-gray-500">Location</span>
          <span className="mx-2 text-gray-400">/</span>
          <Link to="/location" className="text-gray-500 hover:text-gray-700">location</Link>
          <span className="mx-2 text-gray-400">/</span>
          <span className="text-gray-900 font-medium">Edit location</span>
        </nav>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
          <div className="border-b border-gray-200 px-6 py-4">
            <div className="flex flex-wrap gap-2">
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
                    activeTab === tab.id
                      ? 'bg-[#009ef7] text-white shadow-md'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <i className={`${tab.icon}`}></i>
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          <div className="p-4 sm:p-6">
            {activeTab === 'basic' && (
              <div>
                <div className="mb-6">
                  <h3 className="text-xl font-bold text-gray-900">Basic Information</h3>
                  <p className="text-sm text-gray-500 mt-1">Essential location details</p>
                </div>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-6">
            {/* Left Column - Media & Metadata (4 Cols) */}
            <div className="lg:col-span-4 space-y-6">
              <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
                <label className="block text-sm font-semibold text-gray-700 mb-3">Location Image</label>
                <div className="relative group w-full h-48 border-2 border-dashed border-gray-300 rounded-xl overflow-hidden bg-white hover:border-[#009ef7] transition-all flex flex-col items-center justify-center cursor-pointer">
                  {imagePreview ? (
                    <>
                      <img src={imagePreview} alt="Preview" className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <span className="text-white text-xs font-semibold px-3 py-1.5 bg-black bg-opacity-65 rounded-lg">Change Image</span>
                      </div>
                    </>
                  ) : (
                    <div className="flex flex-col items-center justify-center p-4 text-center">
                      <svg className="w-10 h-10 text-gray-400 mb-2 transition-colors group-hover:text-[#009ef7]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <p className="text-xs text-gray-500 font-medium">Click or drag image to upload</p>
                      <p className="text-[10px] text-gray-400 mt-1">PNG, JPG up to 5MB</p>
                    </div>
                  )}
                  <input type="file" accept="image/*" onChange={handleImageChange} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" />
                </div>
              </div>

              <div className="bg-gray-50 p-4 rounded-xl border border-gray-200 space-y-4">
                <h4 className="text-xs font-bold uppercase tracking-wider text-gray-400">Metadata</h4>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">Author</label>
                  <input type="text" name="author" value={formData.author} onChange={handleInputChange} placeholder="Enter author name" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-1 focus:ring-[#009ef7] focus:border-[#009ef7] outline-none transition-all" />
                </div>
              </div>
            </div>

            {/* Right Column - Location details (8 Cols) */}
            <div className="lg:col-span-8 space-y-6">
              <div className="bg-white p-6 rounded-xl border border-gray-200 space-y-4">
                <h4 className="text-xs font-bold uppercase tracking-wider text-gray-400">Location Settings</h4>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Location Name <span className="text-red-500">*</span></label>
                  <input type="text" name="name" value={formData.name} onChange={handleInputChange} placeholder="e.g. Paris" required className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-1 focus:ring-[#009ef7] focus:border-[#009ef7] outline-none transition-all" />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Country <span className="text-red-500">*</span></label>
                    <div className="relative" ref={countryDropdownRef}>
                      <input
                        type="text"
                        value={countrySearch}
                        onChange={(e) => {
                          setCountrySearch(e.target.value);
                          setFormData({ ...formData, countryId: '' });
                          setShowCountryDropdown(true);
                        }}
                        onFocus={() => setShowCountryDropdown(true)}
                        placeholder="Search Country"
                        required={!formData.countryId}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-1 focus:ring-[#009ef7] focus:border-[#009ef7] outline-none transition-all"
                      />
                      {showCountryDropdown && (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto py-1">
                          {filteredCountries.length > 0 ? (
                            filteredCountries.map(country => (
                              <div
                                key={country._id}
                                onClick={() => selectCountry(country)}
                                className="px-3 py-2 hover:bg-slate-50 cursor-pointer text-sm text-gray-700 transition-colors"
                              >
                                {country.name}
                              </div>
                            ))
                          ) : (
                            <div className="px-3 py-2 text-gray-400 text-sm">No countries found</div>
                          )}
                        </div>
                      )}
                    </div>
                    <input type="hidden" name="countryId" value={formData.countryId} required />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">State</label>
                    <div className="relative" ref={stateDropdownRef}>
                      <input
                        type="text"
                        value={stateSearch}
                        onChange={(e) => {
                          setStateSearch(e.target.value);
                          setFormData({ ...formData, stateId: '' });
                          setShowStateDropdown(true);
                        }}
                        onFocus={() => setShowStateDropdown(true)}
                        placeholder="Search State"
                        disabled={!formData.countryId}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-1 focus:ring-[#009ef7] focus:border-[#009ef7] outline-none transition-all disabled:bg-gray-50 disabled:text-gray-400"
                      />
                      {showStateDropdown && formData.countryId && (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto py-1">
                          {filteredStates.length > 0 ? (
                            filteredStates.map(state => (
                              <div
                                key={state._id}
                                onClick={() => selectState(state)}
                                className="px-3 py-2 hover:bg-slate-50 cursor-pointer text-sm text-gray-700 transition-colors"
                              >
                                {state.name}
                              </div>
                            ))
                          ) : (
                            <div className="px-3 py-2 text-gray-400 text-sm">No states found</div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Latitude</label>
                    <input type="number" step="any" name="latitude" value={formData.latitude} onChange={handleInputChange} placeholder="Latitude coordinates" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-1 focus:ring-[#009ef7] focus:border-[#009ef7] outline-none transition-all" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Longitude</label>
                    <input type="number" step="any" name="longitude" value={formData.longitude} onChange={handleInputChange} placeholder="Longitude coordinates" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-1 focus:ring-[#009ef7] focus:border-[#009ef7] outline-none transition-all" />
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-xl border border-gray-200 space-y-4">
                <h4 className="text-xs font-bold uppercase tracking-wider text-gray-400">Associations</h4>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Best Months to Visit</label>
                    <MultiSelect
                      options={months}
                      value={formData.months || []}
                      onChange={(value) => setFormData({ ...formData, months: value })}
                      placeholder="Select months"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Linked Packages</label>
                    <MultiSelect
                      options={packages}
                      value={formData.packages || []}
                      onChange={(value) => setFormData({ ...formData, packages: value })}
                      placeholder="Select packages"
                    />
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-xl border border-gray-200 space-y-4">
                <h4 className="text-xs font-bold uppercase tracking-wider text-gray-400">Brief Overview</h4>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Short Description</label>
                  <textarea name="shortDescription" value={formData.shortDescription} onChange={handleInputChange} placeholder="Enter a short overview text..." rows="4" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-1 focus:ring-[#009ef7] focus:border-[#009ef7] outline-none transition-all resize-none" />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'details' && (
        <div>
          <div className="mb-6">
            <h3 className="text-xl font-bold text-gray-900">Additional Details</h3>
            <p className="text-sm text-gray-500 mt-1">Weather, budget, and travel information</p>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">About</label>
            <JoditEditor ref={aboutEditor} value={about} config={editorConfig} onBlur={newContent => setAbout(newContent)} />
          </div>

          <div className="mb-6">
            <label className="block text-lg font-semibold text-gray-900 mb-4">Weather Information</label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Summer</label>
                <textarea value={formData.weatherInfo.summer} onChange={(e) => setFormData({...formData, weatherInfo: {...formData.weatherInfo, summer: e.target.value}})} placeholder="Summer weather" rows="3" className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Winter</label>
                <textarea value={formData.weatherInfo.winter} onChange={(e) => setFormData({...formData, weatherInfo: {...formData.weatherInfo, winter: e.target.value}})} placeholder="Winter weather" rows="3" className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Monsoon</label>
                <textarea value={formData.weatherInfo.monsoon} onChange={(e) => setFormData({...formData, weatherInfo: {...formData.weatherInfo, monsoon: e.target.value}})} placeholder="Monsoon weather" rows="3" className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent" />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Best Time to Visit</label>
              <input type="text" value={formData.bestTimeToVisit} onChange={(e) => setFormData({...formData, bestTimeToVisit: e.target.value})} placeholder="e.g., October to March" className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Estimated Budget</label>
              <input type="text" value={formData.estimatedBudget} onChange={(e) => setFormData({...formData, estimatedBudget: e.target.value})} placeholder="e.g., ₹15,000 - ₹25,000" className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Language</label>
              <input type="text" value={formData.language} onChange={(e) => setFormData({...formData, language: e.target.value})} placeholder="e.g., Hindi, English" className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Duration</label>
              <input type="text" value={formData.duration} onChange={(e) => setFormData({...formData, duration: e.target.value})} placeholder="e.g., 3-5 days" className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Far From</label>
              <input type="text" value={formData.farFrom} onChange={(e) => setFormData({...formData, farFrom: e.target.value})} placeholder="e.g., 50 km from city center" className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Address</label>
              <input type="text" value={formData.address} onChange={(e) => setFormData({...formData, address: e.target.value})} placeholder="Enter address" className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent" />
            </div>
          </div>

          <div className="mb-6">
            <label className="block text-lg font-semibold text-gray-900 mb-4">Keyword Link Section</label>
            {keywordLinks.map((kl, index) => (
              <div key={index} className="border border-gray-300 rounded-lg p-4 mb-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Keyword</label>
                    <input type="text" value={kl.keyword} onChange={(e) => updateKeywordLink(index, 'keyword', e.target.value)} placeholder="Enter keyword" className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Link</label>
                    <input type="url" value={kl.link} onChange={(e) => updateKeywordLink(index, 'link', e.target.value)} placeholder="Enter link URL" className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent" />
                  </div>
                </div>
                <button type="button" onClick={() => removeKeywordLink(index)} className="px-2 sm:px-4 py-2 text-sm border border-red-500 text-red-500 rounded-lg hover:bg-red-50">Remove</button>
              </div>
            ))}
            <div className="text-right">
              <button type="button" onClick={addKeywordLink} className="px-2 sm:px-4 py-2 text-sm border border-[#009ef7] text-[#009ef7] rounded-lg hover:bg-[#009ef7]/10">Add Keyword Link</button>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'locationFaq' && (
        <div>
          <div className="mb-6">
            <h3 className="text-xl font-bold text-gray-900">Location FAQ Sections</h3>
            <p className="text-sm text-gray-500 mt-1">FAQs for location pages</p>
          </div>

          <div>
            <label className="block text-lg font-semibold text-gray-900 mb-4">FAQ Section</label>
            {faqs.map((faq, index) => (
              <div key={index} className="border border-gray-300 rounded-lg p-4 mb-3">
                <div className="mb-3">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Question</label>
                  <input type="text" value={faq.question} onChange={(e) => updateFaq(index, 'question', e.target.value)} placeholder="Enter FAQ question" className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent" />
                </div>
                <div className="mb-3">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Answer</label>
                  <textarea value={faq.answer} onChange={(e) => updateFaq(index, 'answer', e.target.value)} placeholder="Enter FAQ answer" rows="3" className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent" />
                </div>
                <button type="button" onClick={() => removeFaq(index)} className="px-2 sm:px-4 py-2 text-sm border border-red-500 text-red-500 rounded-lg hover:bg-red-50">Remove FAQ</button>
              </div>
            ))}
            <div className="text-right">
              <button type="button" onClick={addFaq} className="px-2 sm:px-4 py-2 text-sm border border-[#009ef7] text-[#009ef7] rounded-lg hover:bg-[#009ef7]/10">Add FAQ</button>
            </div>
          </div>

          <div className="mt-8">
            <label className="block text-lg font-semibold text-gray-900 mb-4">Recent FAQ Section</label>
            {recentFaqs.map((faq, index) => (
              <div key={index} className="border border-gray-300 rounded-lg p-4 mb-3">
                <div className="mb-3">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Question</label>
                  <input type="text" value={faq.question} onChange={(e) => updateRecentFaq(index, 'question', e.target.value)} placeholder="Enter Recent FAQ question" className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent" />
                </div>
                <div className="mb-3">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Answer</label>
                  <textarea value={faq.answer} onChange={(e) => updateRecentFaq(index, 'answer', e.target.value)} placeholder="Enter Recent FAQ answer" rows="3" className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent" />
                </div>
                <button type="button" onClick={() => removeRecentFaq(index)} className="px-2 sm:px-4 py-2 text-sm border border-red-500 text-red-500 rounded-lg hover:bg-red-50">Remove Recent FAQ</button>
              </div>
            ))}
            <div className="text-right">
              <button type="button" onClick={addRecentFaq} className="px-2 sm:px-4 py-2 text-sm border border-[#009ef7] text-[#009ef7] rounded-lg hover:bg-[#009ef7]/10">Add Recent FAQ</button>
            </div>
          </div>

        </div>
      )}

      {activeTab === 'packageFaq' && (
        <div>
          <div className="mb-6">
            <h3 className="text-xl font-bold text-gray-900">Package FAQ Sections</h3>
            <p className="text-sm text-gray-500 mt-1">FAQs for package pages</p>
          </div>

          <div>
            <label className="block text-lg font-semibold text-gray-900 mb-4">FAQ Package Section</label>
            {faqsPackage.map((faq, index) => (
              <div key={index} className="border border-gray-300 rounded-lg p-4 mb-3">
                <div className="mb-3">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Question</label>
                  <input type="text" value={faq.question} onChange={(e) => updateFaqPackage(index, 'question', e.target.value)} placeholder="Enter question" className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent" />
                </div>
                <div className="mb-3">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Answer</label>
                  <textarea value={faq.answer} onChange={(e) => updateFaqPackage(index, 'answer', e.target.value)} placeholder="Enter answer" rows="3" className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent" />
                </div>
                <button type="button" onClick={() => removeFaqPackage(index)} className="px-2 sm:px-4 py-2 text-sm border border-red-500 text-red-500 rounded-lg hover:bg-red-50">Remove</button>
              </div>
            ))}
            <div className="text-right">
              <button type="button" onClick={addFaqPackage} className="px-2 sm:px-4 py-2 text-sm border border-[#009ef7] text-[#009ef7] rounded-lg hover:bg-[#009ef7]/10">Add FAQ</button>
            </div>
          </div>

          <div className="mt-8">
            <label className="block text-lg font-semibold text-gray-900 mb-4">Recent FAQ Package Section</label>
            {recentFaqsPackage.map((faq, index) => (
              <div key={index} className="border border-gray-300 rounded-lg p-4 mb-3">
                <div className="mb-3">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Question</label>
                  <input type="text" value={faq.question} onChange={(e) => updateRecentFaqPackage(index, 'question', e.target.value)} placeholder="Enter question" className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent" />
                </div>
                <div className="mb-3">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Answer</label>
                  <textarea value={faq.answer} onChange={(e) => updateRecentFaqPackage(index, 'answer', e.target.value)} placeholder="Enter answer" rows="3" className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent" />
                </div>
                <button type="button" onClick={() => removeRecentFaqPackage(index)} className="px-2 sm:px-4 py-2 text-sm border border-red-500 text-red-500 rounded-lg hover:bg-red-50">Remove</button>
              </div>
            ))}
            <div className="text-right">
              <button type="button" onClick={addRecentFaqPackage} className="px-2 sm:px-4 py-2 text-sm border border-[#009ef7] text-[#009ef7] rounded-lg hover:bg-[#009ef7]/10">Add FAQ</button>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'thingsToDoFaq' && (
        <div>
          <div className="mb-6">
            <h3 className="text-xl font-bold text-gray-900">Things To Do FAQ Sections</h3>
            <p className="text-sm text-gray-500 mt-1">FAQs for things to do pages</p>
          </div>

          <div>
            <label className="block text-lg font-semibold text-gray-900 mb-4">FAQ Things To Do Section</label>
            {faqsThingsToDo.map((faq, index) => (
              <div key={index} className="border border-gray-300 rounded-lg p-4 mb-3">
                <div className="mb-3">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Question</label>
                  <input type="text" value={faq.question} onChange={(e) => updateFaqThingsToDo(index, 'question', e.target.value)} placeholder="Enter question" className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent" />
                </div>
                <div className="mb-3">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Answer</label>
                  <textarea value={faq.answer} onChange={(e) => updateFaqThingsToDo(index, 'answer', e.target.value)} placeholder="Enter answer" rows="3" className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent" />
                </div>
                <button type="button" onClick={() => removeFaqThingsToDo(index)} className="px-2 sm:px-4 py-2 text-sm border border-red-500 text-red-500 rounded-lg hover:bg-red-50">Remove</button>
              </div>
            ))}
            <div className="text-right">
              <button type="button" onClick={addFaqThingsToDo} className="px-2 sm:px-4 py-2 text-sm border border-[#009ef7] text-[#009ef7] rounded-lg hover:bg-[#009ef7]/10">Add FAQ</button>
            </div>
          </div>

          <div className="mt-8">
            <label className="block text-lg font-semibold text-gray-900 mb-4">Recent FAQ Things To Do Section</label>
            {recentFaqsThingsToDo.map((faq, index) => (
              <div key={index} className="border border-gray-300 rounded-lg p-4 mb-3">
                <div className="mb-3">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Question</label>
                  <input type="text" value={faq.question} onChange={(e) => updateRecentFaqThingsToDo(index, 'question', e.target.value)} placeholder="Enter question" className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent" />
                </div>
                <div className="mb-3">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Answer</label>
                  <textarea value={faq.answer} onChange={(e) => updateRecentFaqThingsToDo(index, 'answer', e.target.value)} placeholder="Enter answer" rows="3" className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent" />
                </div>
                <button type="button" onClick={() => removeRecentFaqThingsToDo(index)} className="px-2 sm:px-4 py-2 text-sm border border-red-500 text-red-500 rounded-lg hover:bg-red-50">Remove</button>
              </div>
            ))}
            <div className="text-right">
              <button type="button" onClick={addRecentFaqThingsToDo} className="px-2 sm:px-4 py-2 text-sm border border-[#009ef7] text-[#009ef7] rounded-lg hover:bg-[#009ef7]/10">Add FAQ</button>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'placesToVisitFaq' && (
        <div>
          <div className="mb-6">
            <h3 className="text-xl font-bold text-gray-900">Places To Visit FAQ Sections</h3>
            <p className="text-sm text-gray-500 mt-1">FAQs for places to visit pages</p>
          </div>

          <div>
            <label className="block text-lg font-semibold text-gray-900 mb-4">FAQ Places To Visit Section</label>
            {faqPlacesToVisit.map((faq, index) => (
              <div key={index} className="border border-gray-300 rounded-lg p-4 mb-3">
                <div className="mb-3">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Question</label>
                  <input type="text" value={faq.question} onChange={(e) => updateFaqPlacesToVisit(index, 'question', e.target.value)} placeholder="Enter question" className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent" />
                </div>
                <div className="mb-3">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Answer</label>
                  <textarea value={faq.answer} onChange={(e) => updateFaqPlacesToVisit(index, 'answer', e.target.value)} placeholder="Enter answer" rows="3" className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent" />
                </div>
                <button type="button" onClick={() => removeFaqPlacesToVisit(index)} className="px-2 sm:px-4 py-2 text-sm border border-red-500 text-red-500 rounded-lg hover:bg-red-50">Remove</button>
              </div>
            ))}
            <div className="text-right">
              <button type="button" onClick={addFaqPlacesToVisit} className="px-2 sm:px-4 py-2 text-sm border border-[#009ef7] text-[#009ef7] rounded-lg hover:bg-[#009ef7]/10">Add FAQ</button>
            </div>
          </div>

          <div className="mt-8">
            <label className="block text-lg font-semibold text-gray-900 mb-4">Recent FAQ Places To Visit Section</label>
            {recentFaqPlacesToVisit.map((faq, index) => (
              <div key={index} className="border border-gray-300 rounded-lg p-4 mb-3">
                <div className="mb-3">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Question</label>
                  <input type="text" value={faq.question} onChange={(e) => updateRecentFaqPlacesToVisit(index, 'question', e.target.value)} placeholder="Enter question" className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent" />
                </div>
                <div className="mb-3">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Answer</label>
                  <textarea value={faq.answer} onChange={(e) => updateRecentFaqPlacesToVisit(index, 'answer', e.target.value)} placeholder="Enter answer" rows="3" className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent" />
                </div>
                <button type="button" onClick={() => removeRecentFaqPlacesToVisit(index)} className="px-2 sm:px-4 py-2 text-sm border border-red-500 text-red-500 rounded-lg hover:bg-red-50">Remove</button>
              </div>
            ))}
            <div className="text-right">
              <button type="button" onClick={addRecentFaqPlacesToVisit} className="px-2 sm:px-4 py-2 text-sm border border-[#009ef7] text-[#009ef7] rounded-lg hover:bg-[#009ef7]/10">Add Recent FAQ</button>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'seo' && (
        <div>
          <div className="mb-6">
            <h3 className="text-xl font-bold text-gray-900">SEO Meta Information</h3>
            <p className="text-sm text-gray-500 mt-1">Optimize for search engines</p>
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">Meta Title</label>
            <input type="text" name="metaTitle" value={formData.metaTitle} onChange={handleInputChange} placeholder="Enter meta title" className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent" />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">Meta Script (Schema)</label>
            <textarea name="metaScript" value={formData.metaScript} onChange={handleInputChange} placeholder="Type your text here..." rows="3" className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent" />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">Meta Keywords</label>
            <textarea name="metaKeyword" value={formData.metaKeyword} onChange={handleInputChange} placeholder="Enter meta keywords" rows="3" className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent" />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">Meta Description</label>
            <textarea name="metaDescription" value={formData.metaDescription} onChange={handleInputChange} placeholder="Enter meta Description" rows="3" className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent" />
          </div>
        </div>
      )}
    </div>
  </div>

        <div className="flex justify-end gap-3 mt-6">
          <button type="button" onClick={() => setShowBackModal(true)} className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">Cancel</button>
          <button type="submit" disabled={submitting} className="px-6 py-2 bg-[#009ef7] text-white rounded-lg hover:bg-[#009ef7]/90 disabled:opacity-50">
            {submitting ? 'Updating...' : 'Update'}
          </button>
        </div>
      </form>

      <ConfirmModal
        isOpen={showBackModal}
        title="Are you sure you want to go back?"
        message="Any unsaved changes to this location will be lost."
        confirmText="Yes, Go Back"
        cancelText="Stay Here"
        onConfirm={() => navigate('/location')}
        onCancel={() => setShowBackModal(false)}
      />
    </div>
  );
};

export default EditLocation;
